/**
 * Files view rendering — the DOM half of the pane. Turns the in-memory queue into
 * cards, keeps the send button's enabled state in sync, and shows the status line.
 * Reads `state`, writes the DOM; no fetch and no Office.js here. The remove control
 * carries `data-oa-remove` and the row's id, so the controller wires removal via
 * event delegation (like the other oa-* actions) rather than the view reaching into
 * the action layer.
 */

import { getSessionId } from "../../js/utils/roaming_settings.js";
import { formatFileSize } from "./secure_link.js";
import { state } from "./files_state.js";

const FILE_ICON =
  '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/></svg>';
const REMOVE_ICON =
  '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8"/></svg>';

export function renderQueue() {
  const list = document.getElementById("file-list");
  if (!list) return;
  list.replaceChildren(...state.queue.map(buildCard));
  // The send button's enabled state is a function of the queue — re-evaluate on
  // every render so it tracks adds, removals, and upload completion.
  updateSendButtonState();
}

function buildCard(entry) {
  const li = document.createElement("li");
  li.dataset.id = entry.id;
  li.className = "border border-border-subtle rounded-md bg-surface-raised px-2.5 py-1.5";

  const row = document.createElement("div");
  row.className = "flex items-center gap-2 min-w-0";

  const icon = document.createElement("span");
  icon.className = "shrink-0 text-text-tertiary";
  icon.innerHTML = FILE_ICON;

  const name = document.createElement("span");
  name.className = "flex-1 min-w-0 truncate text-sm font-medium text-text-primary";
  name.title = entry.name;
  name.textContent = entry.name;

  const size = document.createElement("span");
  size.className = "shrink-0 text-xs text-text-tertiary whitespace-nowrap";
  size.textContent = formatFileSize(entry.size);

  row.append(icon, name, size);

  // Status badge — none for the happy "uploaded" state.
  if (entry.status === "attached") {
    // Sent: this file is in the secure link. Show a muted "Attached" marker; the
    // row is still editable (remove control below).
    const status = document.createElement("span");
    status.className = "shrink-0 text-xs whitespace-nowrap text-teal-600";
    status.textContent = "Attached";
    row.append(status);
  } else if (entry.status !== "uploaded") {
    const status = document.createElement("span");
    status.dataset.status = "";
    status.className = "shrink-0 text-xs whitespace-nowrap";
    if (entry.status === "uploading") {
      status.classList.add("text-teal-600");
      status.textContent = `${entry.percent ?? 0}%`;
    } else if (entry.status === "moving") {
      // Being read out of the email — no progress to report until the upload starts.
      status.classList.add("text-teal-600");
      status.textContent = "Moving";
    } else if (entry.status === "error") {
      status.classList.add("text-status-danger-text");
      status.textContent = "Failed";
    } else {
      status.classList.add("text-text-tertiary");
      status.textContent = "Queued";
    }
    row.append(status);
  }

  const remove = document.createElement("button");
  remove.type = "button";
  remove.dataset.oaRemove = "";
  remove.className = "shrink-0 text-text-tertiary hover:text-text-primary cursor-pointer";
  remove.title = "Remove";
  remove.setAttribute("aria-label", `Remove ${entry.name}`);
  remove.innerHTML = REMOVE_ICON;
  row.append(remove);

  li.append(row);

  if (entry.status === "uploading") {
    const track = document.createElement("div");
    track.className = "mt-1.5 h-[3px] bg-surface-sunken rounded-full overflow-hidden";
    const bar = document.createElement("div");
    bar.dataset.bar = "";
    bar.className = "h-full bg-teal-400 rounded-full transition-[width] duration-150";
    bar.style.width = `${entry.percent ?? 0}%`;
    track.append(bar);
    li.append(track);
  } else if (entry.status === "error" && entry.error) {
    const err = document.createElement("p");
    err.className = "mt-1 m-0 text-xs text-status-danger-text";
    err.textContent = entry.error;
    li.append(err);
  }

  return li;
}

/** Targeted progress update during upload (avoids a full re-render per event). */
export function updateProgress(id, percent) {
  const entry = state.queue.find((e) => e.id === id);
  if (entry) entry.percent = percent;
  const li = document.querySelector(`#file-list li[data-id="${id}"]`);
  if (!li) return;
  const bar = li.querySelector("[data-bar]");
  if (bar) bar.style.width = percent + "%";
  const status = li.querySelector("[data-status]");
  if (status) status.textContent = percent + "%";
}

// Enabled only with a live session and at least one fully-uploaded file (and no
// send in flight). Recipients are validated at click, not here — a failed check
// arms a RecipientsChanged retry in the action layer instead of gating the button.
export function updateSendButtonState() {
  // Once this session has a transfer, the pane is in "editing" mode: the file
  // list stays editable (Add files appends to it, ✕ removes from it) and the
  // primary is a re-attach recovery for the link this draft already has (enabled
  // so a deleted link can be restored). Add files always keeps its label.
  const attachedState = !!state.lastAttached;
  const staged = state.queue.filter((e) => e.status !== "attached");

  const btn = document.querySelector("[data-oa-send]");
  if (!btn) return;
  if (attachedState) {
    btn.textContent = "Re-attach files securely";
    btn.disabled = state.sending;
    return;
  }
  btn.textContent = "Attach files securely";
  // movingAttachments matters as much as sending: between two moved attachments
  // the queue looks complete, and sending there would leave the rest behind.
  const ready =
    !state.sending &&
    !state.movingAttachments &&
    !!getSessionId() &&
    staged.length > 0 &&
    staged.every((e) => e.status === "uploaded" && !!e.oid);
  btn.disabled = !ready;
}

export function showSendStatus(message, ok) {
  const el = document.querySelector("[data-oa-send-status]");
  if (!el) return;
  el.textContent = message;
  el.classList.remove("hidden", "text-status-danger-text", "text-teal-600", "font-medium");
  // Failures need the user to act (e.g. "press Enter" on an unconfirmed
  // recipient) — a weight bump over the confirmation notes, same size.
  el.classList.add(...(ok ? ["text-teal-600"] : ["text-status-danger-text", "font-medium"]));
}

export function clearSendStatus() {
  const el = document.querySelector("[data-oa-send-status]");
  if (!el) return;
  el.classList.add("hidden");
  el.textContent = "";
}

// Suggestion banner for the draft's own attachments once they cross the threshold.
// Rendered here rather than server-side because the offer depends on what Outlook
// is holding at this moment; the template only provides the container.
export function renderAttachmentBanner() {
  const host = document.querySelector("[data-oa-attachment-banner]");
  if (!host) return;
  const attachments = state.oversizeAttachments;
  if (attachments.length === 0) {
    host.replaceChildren();
    host.classList.add("hidden");
    return;
  }

  const total = formatFileSize(attachments.reduce((sum, a) => sum + (a.size || 0), 0));
  const text = document.createElement("p");
  text.className = "m-0 text-xs text-text-secondary";
  text.textContent =
    attachments.length === 1
      ? `This attachment is ${total}. Send it as a secure LeapFILE link instead?`
      : `These ${attachments.length} attachments total ${total}. Send them as a secure LeapFILE link instead?`;

  const move = document.createElement("button");
  move.type = "button";
  move.dataset.oaMoveAttachments = "";
  move.className =
    "font-medium focus:outline-hidden transition-colors cursor-pointer bg-teal-600 hover:bg-teal-400 " +
    "active:bg-teal-800 text-white border border-transparent rounded-md inline-flex items-center " +
    "justify-center h-7 px-2.5 text-xs focus:ring-2 focus:ring-teal-600 focus:ring-offset-2";
  move.textContent = "Move to LeapFILE";

  const dismiss = document.createElement("button");
  dismiss.type = "button";
  dismiss.dataset.oaDismissAttachments = "";
  dismiss.className =
    "font-medium focus:outline-hidden transition-colors cursor-pointer bg-surface-raised " +
    "hover:bg-surface-muted active:bg-surface-sunken text-text-primary border border-border-subtle " +
    "hover:border-border rounded-md inline-flex items-center justify-center h-7 px-2.5 text-xs";
  dismiss.textContent = "Not now";

  const actions = document.createElement("div");
  actions.className = "flex gap-2";
  actions.append(move, dismiss);

  host.replaceChildren(text, actions);
  host.classList.remove("hidden");
}
